import { AnimatePresence, motion } from "motion/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useModals } from "react-easy-modals";
import Block from "@/components/block";
import Input from "@/components/input";
import List from "@/components/list";
import Modal from "@/components/modal";
import { CATEGORIES_PATH } from "@/config/index";
import useBreakpoints from "@/modules/hooks/use-breakpoints";
import useIntersectionObserver from "@/modules/hooks/use-intersection-observer";
import { MAX_TITLE_LENGTH } from "@/routes/jobs/new/-constants";
import polyglot from "@/utils/polyglot";
import { colors, shadows, spacings } from "../../assets/themes";
import { api, getCategoryFormUrlById, uuid } from "../../utils/index";
import { Body18 } from "../text";
import CategoriesNavigationSkeleton from "./categories-navigation-skeleton";
import CustomAskItem from "./custom-ask-item";
import MainCategoryItem from "./main-category-item";
import { launchSearch } from "./search-algo";
import SearchBreadcrumb from "./search-breadcrumb";
import SearchItem from "./search-item";
import SelectCategoryModal from "./select-category-modal";
import {
  getCategoriesPathById,
  getCategoryDataById,
  getFormatedCategoriesPathForPrivate,
  getUrlParamsForPrivate,
} from "./utils";

let to;

const MIN_SEARCH_LENGTH = 2;
function CategoriesNavigationModal({
  isOpen,
  close,
  url_params,
  user,
  subcategories,
  category_id,
}) {
  const anchorRef = useRef<HTMLDivElement>(null);
  const [navigationData, setNavigationData] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [inputIsLoading, setInputIsLoading] = useState(false);
  const [sessionId] = useState(uuid());
  const inputRef = useRef();
  const [currentCategoryId, setCurrentCategoryId] = useState(category_id);
  const breakpoints = useBreakpoints();
  const searchMode = inputRef.current?.value?.length > MIN_SEARCH_LENGTH;
  const currId = currentCategoryId;
  const [isStickyFloating, setisStickyFloating] = useState(false);
  const modals = useModals();

  useIntersectionObserver({
    target: anchorRef,
    onObserver: (entry) => {
      setisStickyFloating(!entry.isIntersecting);
    },
  });

  const categoriesWithPrivate = useMemo(
    () => getFormatedCategoriesPathForPrivate(subcategories, CATEGORIES_PATH),
    [subcategories, CATEGORIES_PATH]
  );

  useEffect(() => {
    if (currId && inputRef.current) {
      setSearchResults([]);
      inputRef.current.value = "";
    }
    setNavigationData(getCategoryDataById(currId, categoriesWithPrivate));
  }, [currId, categoriesWithPrivate]);

  useEffect(() => {
    if (searchMode) {
      setCurrentCategoryId(null);
    }
  }, [searchMode]);

  const sendSearchTerm = (data) => {
    if (data && data.term.length <= 100) {
      api.post("/searchbar", data);
    }
  };

  const handleChange = (e) => {
    e.persist();
    clearTimeout(to);
    if (e.target?.value?.length > MIN_SEARCH_LENGTH) {
      setInputIsLoading(true);
      to = setTimeout(() => {
        setInputIsLoading(false);
        sendSearchTerm({ term: e.target.value, session: sessionId });
        setSearchResults(launchSearch(e.target.value, categoriesWithPrivate));
      }, 500);
    } else {
      setSearchResults([]);
      setInputIsLoading(false);
    }
  };

  const getNavigationItems = useCallback(
    (navigation) =>
      currId || searchMode ? (
        navigation.map((elem, i) => (
          <div key={`${elem.id}-${i}-navigation-item-transition`}>
            {!elem.children && elem.isCustom && currId ? (
              <CustomAskItem
                href={getCategoryFormUrlById(
                  elem.id,
                  getUrlParamsForPrivate(url_params, {
                    category: elem,
                    user,
                  })
                )}
              />
            ) : (
              <SearchItem
                chevron
                elem={elem}
                onClick={() => setCurrentCategoryId(elem.id)}
                url_params={getUrlParamsForPrivate(url_params, {
                  category: elem,
                  user,
                })}
              />
            )}
          </div>
        ))
      ) : (
        <Block marginBottom={{ xs: 0, sm: `calc(${spacings.m} * -1)` }} marginTop={spacings.s}>
          <Modal.Item.Wrapper>
            <Block
              display="grid"
              gap={spacings.m}
              gridCols={{ xs: 1, md: 2 }}
              marginBottom={{ xs: 0, sm: spacings.m }}
            >
              {navigation.map((elem) => (
                <MainCategoryItem
                  elem={elem}
                  key={`transition-main-${elem.id}`}
                  onClick={() => setCurrentCategoryId(elem.id)}
                />
              ))}
            </Block>
          </Modal.Item.Wrapper>
        </Block>
      ),
    [currId, searchMode, subcategories, setCurrentCategoryId, url_params, categoriesWithPrivate]
  );

  const getNavigationWithPrivate = useCallback(
    (categories = navigationData) => {
      const privateCategories = categories.filter((e) => e.eligible_private);
      const publicCategories = categories.filter((e) => !e.eligible_private);
      return (
        <>
          {privateCategories.length > 0 && (
            <>
              <List.Header withGutters>
                {polyglot.t("categories_modal.first_name_services", {
                  first_name: user?.first_name,
                })}
              </List.Header>
              {getNavigationItems(privateCategories)}
              {publicCategories.length > 0 && (
                <List.Header withGutters>
                  {polyglot.t("categories_modal.other_services")}
                </List.Header>
              )}
            </>
          )}
          {publicCategories.length > 0 && getNavigationItems(publicCategories)}
        </>
      );
    },
    [navigationData, getNavigationItems, user, subcategories]
  );

  const getSearchResults = useCallback(() => {
    const formatedSearchResult = searchResults.map(({ item }) => item);
    return (
      <>
        {getNavigationWithPrivate(formatedSearchResult)}
        <CustomAskItem
          onClick={() =>
            modals.open(SelectCategoryModal, {
              formatedCategories: categoriesWithPrivate,
              urlParams: url_params,
              user,
            })
          }
        />
      </>
    );
  }, [searchResults, getNavigationWithPrivate]);

  const handleBack = () => {
    const paths = getCategoriesPathById(currId);
    setCurrentCategoryId(paths[paths.length - 2]);
  };

  return (
    <Modal.Medium
      animate={modals.action === "push"}
      css={`
        > div:last-of-type {
          align-items: flex-start;
          > div {
            margin-top: ${breakpoints.get({ xs: 0, sm: "4vh" })};
            margin-bottom: ${breakpoints.get({ xs: 0, sm: "4vh" })};
            max-height: ${breakpoints.get({ xs: "inherit", sm: "92vh" })};
            display: block;
            overflow-y: ${breakpoints.get({ xs: "initial", sm: "auto" })};
          }
        }
      `}
      forceMount
      fullScreenOnMobile
      isOpen={isOpen}
      onClose={close}
    >
      <div ref={anchorRef} />
      <Block
        background={colors.background}
        boxShadow={isStickyFloating ? shadows.s : "0px 0px 0px rgba(0, 0, 0, 0)"}
        paddingBottom={{ xs: spacings.s, sm: 0 }}
        position="sticky"
        top="0"
        transition="box-shadow 0.2s ease-out"
        zIndex="100"
      >
        <Modal.Item.Header
          navBarContent={() => (
            <>
              <Block
                display={{ xs: "block", sm: "none" }}
                left="0"
                pointerEvents="none"
                position="absolute"
                right="0"
                textAlign="center"
              >
                <Body18 align="center" strong>
                  {currId
                    ? polyglot.t(`categories.${currId}.name`)
                    : polyglot.t("jobs.ask_service")}
                </Body18>
              </Block>
            </>
          )}
          onBack={currId ? handleBack : null}
          onClose={close}
        >
          {breakpoints.get({
            xs: null,
            sm: (
              <Modal.Item.Title>
                {currId ? polyglot.t(`categories.${currId}.name`) : polyglot.t("jobs.ask_service")}
              </Modal.Item.Title>
            ),
          })}
        </Modal.Item.Header>
        <Block marginTop={{ xs: spacings.xs, sm: null }}>
          <Modal.Item.Wrapper>
            <Input
              isLoading={inputIsLoading}
              maxLength={MAX_TITLE_LENGTH}
              onChange={handleChange}
              placeholder={polyglot.t("ask_service.search_job")}
              ref={inputRef}
              type="search"
            />
          </Modal.Item.Wrapper>
        </Block>
        <Modal.Item.Wrapper>
          <Block
            display={{ xs: "none", sm: "block" }}
            marginBottom={spacings.xs}
            marginTop={spacings.sm}
          >
            {!searchMode && <SearchBreadcrumb currId={currId} onClick={setCurrentCategoryId} />}
          </Block>
        </Modal.Item.Wrapper>
      </Block>
      <Block display="flex" flexDirection="column" overflow="hidden">
        {inputIsLoading && searchResults.length === 0 ? (
          <Modal.Item.Wrapper>
            <CategoriesNavigationSkeleton />
          </Modal.Item.Wrapper>
        ) : (
          <AnimatePresence initial={false} mode="wait">
            <motion.div
              animate={{
                x: 0,
                opacity: 1,
              }}
              initial={{
                x: 50,
                opacity: 0,
              }}
              key={`animate-${currId}`}
              transition={{ duration: 0.2, ease: [1, 0, 0, 1] }}
            >
              {searchMode ? getSearchResults() : getNavigationWithPrivate()}
            </motion.div>
          </AnimatePresence>
        )}
      </Block>
    </Modal.Medium>
  );
}

export default CategoriesNavigationModal;
